<template>
  <app-page-subsection
    id="ratings"
    class="@container"
    :title="{ tag: 'h3', text: $t('course.page.ratings.title') }"
  >
    <template v-if="course.ratings" #append-title>
      <span class="ml-2 font-normal">({{ course.ratings?.count }})</span>
    </template>

    <div class="grid gap-6 @2xl:grid-cols-[1fr_auto_2fr]">
      <div class="basis-[250px]">
        <section>
          <h3 :class="ratingSectionTitleClasses">
            {{ $t("course.page.ratings.title_global") }}
          </h3>

          <p class="flex items-center gap-2">
            <app-rating-label
              :size="32"
              :value="ratings.statistics.global_satisfaction"
            />
            <app-rating-stars
              :size="32"
              :value="ratings.statistics.global_satisfaction"
            />
          </p>
        </section>

        <section class="mt-8">
          <h3 :class="ratingSectionTitleClasses">
            {{ $t("course.page.ratings.title_details") }}
          </h3>

          <rating-details-table :details="ratings.statistics" tooltips />
        </section>
      </div>

      <app-divider vertical />

      <section v-if="displayedRatings.length > 0">
        <h3 :class="ratingSectionTitleClasses">
          {{ $t("course.page.ratings.title_list") }}
        </h3>
        <course-rating-item-list :list="displayedRatings" />

        <div class="mt-8 grid place-content-center">
          <app-button
            v-if="ratingsDialogNeeded"
            color="dark"
            size="small"
            variant="outlined"
            @click="toggleRatingDialog(true)"
          >
            {{ $t("course.page.ratings.view_all_ratings_label") }}
          </app-button>
        </div>
      </section>

      <p v-else class="grid place-content-center font-semibold">
        <app-image
          alt=""
          class="mx-auto mb-6"
          src="/images/illustrations/empty_ratings.svg"
        />
        {{ $t("course.page.ratings.empty_label") }}
      </p>
    </div>

    <lazy-app-dialog
      v-if="showDialog"
      v-model="showDialog"
      :title="$t('course.page.ratings.dialog.title')"
      :width="700"
    >
      <course-rating-item-list :list="ratings.list" />
    </lazy-app-dialog>
  </app-page-subsection>
</template>

<script lang="ts" setup>
import CourseRatingItemList from "~/business-areas/course/components/course-rating-item-list.vue";
import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CourseRatingsView } from "~~/model/course.model";

const RATING_WITH_COMMENT_DISPLAY_LIMIT = 4;

const properties = defineProps<{
  course: CoursePageView;
  ratings: CourseRatingsView;
}>();

const displayedRatings = computed(() =>
  properties.ratings.list.slice(0, RATING_WITH_COMMENT_DISPLAY_LIMIT),
);

const ratingsDialogNeeded = computed(
  () => properties.ratings.list.length > displayedRatings.value.length,
);
const [showDialog, toggleRatingDialog] = useToggle();

const ratingSectionTitleClasses = "mb-4  font-bold";
</script>
